import React, { useState, useEffect } from 'react';
import { Card, List, Typography, Tag, Spin, Empty, Button, Space } from 'antd';
import { 
  FileTextOutlined, 
  LinkOutlined, 
  ReloadOutlined,
  SmileOutlined,
  FrownOutlined,
  MehOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { API_SERVIDOR2 } from '../config/api';

const { Text, Paragraph } = Typography;

interface Noticia {
  titulo: string;
  link: string;
  snippet: string;
  fonte: string;
  data: string | null;
  sentimento: 'positivo' | 'negativo' | 'neutro' | null;
}

interface CompanyNewsCardProps {
  cnpj: string;
  autoLoad?: boolean;
}

export const CompanyNewsCard: React.FC<CompanyNewsCardProps> = ({ 
  cnpj, 
  autoLoad = false 
}) => {
  const [noticias, setNoticias] = useState<Noticia[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchNoticias = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${API_SERVIDOR2.BASE}/api/v1/ia/noticias/${cnpj}`,
        { params: { limite: 10 } }
      );
      
      setNoticias(response.data.noticias || []);
      setQuery(response.data.query || '');
      
      if (response.data.error) {
        setError(response.data.error);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao buscar notícias');
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  useEffect(() => {
    if (autoLoad && cnpj && !hasLoaded) {
      fetchNoticias();
    }
  }, [autoLoad, cnpj, hasLoaded]);

  useEffect(() => {
    setHasLoaded(false);
    setNoticias([]);
    setError(null);
  }, [cnpj]);

  const getSentimentIcon = (sentimento: string | null) => {
    switch (sentimento) {
      case 'positivo':
        return <SmileOutlined style={{ color: '#52c41a' }} />;
      case 'negativo':
        return <FrownOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <MehOutlined style={{ color: '#faad14' }} />;
    }
  };

  const getSentimentTag = (sentimento: string | null) => {
    switch (sentimento) {
      case 'positivo':
        return <Tag color="success">Positivo</Tag>;
      case 'negativo':
        return <Tag color="error">Negativo</Tag>;
      default:
        return <Tag color="warning">Neutro</Tag>;
    }
  };

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined />
          <span>Notícias da Empresa</span>
          {query && (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 'normal' }}>
              ({noticias.length} resultados)
            </Text>
          )}
        </Space>
      }
      extra={
        <Button
          icon={<ReloadOutlined spin={loading} />}
          onClick={fetchNoticias}
          loading={loading}
          size="small"
        >
          {hasLoaded ? 'Atualizar' : 'Buscar'}
        </Button>
      }
      style={{ marginBottom: 16 }}
      styles={{ body: { maxHeight: 400, overflowY: 'auto' } }}
    >
      {loading && !noticias.length && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
          <br />
          <Text type="secondary">Buscando notícias...</Text>
        </div>
      )}

      {error && !noticias.length && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={error}
        />
      )}

      {!loading && !error && !hasLoaded && (
        <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
          <FileTextOutlined style={{ fontSize: 32, marginBottom: 8 }} />
          <br />
          <Text type="secondary">
            Clique em "Buscar" para ver notícias relacionadas à empresa.
          </Text>
        </div>
      )}

      {noticias.length > 0 && (
        <List
          itemLayout="vertical"
          dataSource={noticias}
          renderItem={(noticia, index) => (
            <List.Item
              key={index}
              style={{ 
                borderLeft: `3px solid ${
                  noticia.sentimento === 'positivo' ? '#52c41a' : 
                  noticia.sentimento === 'negativo' ? '#ff4d4f' : '#faad14'
                }`,
                paddingLeft: 12,
                marginBottom: 8
              }}
            >
              <div>
                <Space size="small" style={{ marginBottom: 4 }}>
                  {noticia.data && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {noticia.data}
                    </Text>
                  )}
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {noticia.fonte}
                  </Text>
                </Space>
                <br />
                <a 
                  href={noticia.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ fontWeight: 500 }}
                >
                  {noticia.titulo}
                </a>
                <Paragraph 
                  type="secondary" 
                  style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}
                  ellipsis={{ rows: 2 }}
                >
                  {noticia.snippet}
                </Paragraph>
                <a 
                  href={noticia.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ fontSize: 12 }}
                >
                  Ver notícia →
                </a>
              </div>
            </List.Item>
          )}
        />
      )}

      {hasLoaded && !loading && noticias.length === 0 && !error && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nenhuma notícia encontrada"
        />
      )}
    </Card>
  );
};

export default CompanyNewsCard;
